import * as React from 'react'
// libs
import { useQuery, useMutation } from '@apollo/client/react'
// graphql
import { AI_CONVERSATIONS_QUERY } from 'graphql/ai/queries'
import { AI_CONVERSATION_DELETE_MUTATION } from 'graphql/ai/mutations'
// types
import { IAiConversation } from 'types/graphql'
import { AiChatContext, AiConversation } from './types'
// utils
import { getContextKey, getContextVariables } from './utils'

type ConversationsContextValue = {
  loading: boolean
  context: AiChatContext
  conversations: AiConversation[]
  riskTemplateId: string | null
  deleteConversation: (id: string) => Promise<void>
  refetchConversations: () => void
  currentConversationId: string | null
  setCurrentConversationId: (id: string | null) => void
}

type AiConversationsResponse = {
  aiConversations: {
    data: IAiConversation[]
  }
}

export const ConversationsContext = React.createContext<ConversationsContextValue | null>(null)

// Persist current conversation ID per context across modal open/close cycles.
// `undefined` — nothing picked yet (fall back to the most recent conversation),
// `null` — the user explicitly started a new chat.
const persistedIds: Record<string, string | null | undefined> = {}

type Props = {
  context: AiChatContext
  children: React.ReactNode
}

export const ConversationsProvider: React.FC<Props> = ({ context, children }) => {
  const contextKey = getContextKey(context)

  const { data, loading, refetch } = useQuery<AiConversationsResponse>(AI_CONVERSATIONS_QUERY, {
    variables: { pagination: { pageTake: 100, pageSkip: 0 }, filter: getContextVariables(context) },
    fetchPolicy: 'network-only'
  })

  const [deleteConversationMutation] = useMutation(AI_CONVERSATION_DELETE_MUTATION)

  const [selectedId, setSelectedId] = React.useState<string | null | undefined>(() => persistedIds[contextKey])

  // When the context changes, reset to the selection persisted for it
  React.useEffect(() => {
    setSelectedId(persistedIds[contextKey])
  }, [contextKey])

  const conversations: AiConversation[] = data?.aiConversations?.data ?? []

  // Derived synchronously — the risk chat decides whether to summarise based on this
  // right after the list resolves, so it must not lag a render behind.
  const currentConversationId = selectedId === undefined ? (conversations[0]?.id ?? null) : selectedId

  const setCurrentConversationId = React.useCallback(
    (id: string | null) => {
      persistedIds[contextKey] = id
      setSelectedId(id)
    },
    [contextKey]
  )

  const deleteConversation = React.useCallback(
    async (id: string) => {
      await deleteConversationMutation({ variables: { id } })

      const updated = await refetch()
      const remaining: AiConversation[] = updated.data?.aiConversations?.data ?? []

      if (currentConversationId === id) {
        const nextId = remaining[0]?.id ?? null
        persistedIds[contextKey] = nextId
        setSelectedId(nextId)
      }
    },
    [currentConversationId, contextKey, deleteConversationMutation, refetch]
  )

  return (
    <ConversationsContext.Provider
      value={{
        loading,
        context,
        conversations,
        deleteConversation,
        setCurrentConversationId,
        currentConversationId,
        refetchConversations: refetch,
        riskTemplateId: context.riskTemplateId
      }}>
      {children}
    </ConversationsContext.Provider>
  )
}

export const useConversations = (): ConversationsContextValue => {
  const ctx = React.useContext(ConversationsContext)

  if (!ctx) {
    throw new Error('useConversations must be used within ConversationsProvider')
  }

  return ctx
}
